import React, { useState, useEffect } from 'react';
import { OpenAI } from 'openai';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { generatePrompt as generateContemplationPrompt } from '../utils/aiPrompt';
import { generatePrompt as generateBusinessPrompt } from '../utils/aiPromptKD';
import { generatePrompt as generateRoleplayPrompt } from '../utils/aiPromptRoleplay';
import { generatePrompt as generatePlanPrompt } from '../utils/aiPromptPlan';
import './AIAnalysis.css';
import { GoogleGenerativeAI } from "@google/generative-ai";
import { Anthropic } from '@anthropic-ai/sdk';
import { incrementUserAnalysisCount, getUserAnalysisCount, auth, getUserData, doc, updateDoc, db } from '../firebase';
import VIPFeatures from './VIPFeatures';
import VIPPricing from './VIPPricing';
import AIChat from './AIChat';
import { Switch } from './Switch';
import { generateEnhancePrompt } from '../utils/enhancePrompt';
import { Tooltip } from './Tooltip';
import { generateEnhancePrompt as generateEnhanceInteractionGoalPrompt } from '../utils/enhanceInteractionGoal';

const AIAnalysis = ({ 
  hexagramData, 
  resetAnalysis, 
  onResetComplete, 
  phoneNumber, 
  onPromptChange, 
  selectedModel, 
  onModelChange, 
  showAIModelSelection, 
  dominantEnergy, 
  combinedPairs, 
  notificationList, 
  ghiChuList, 
  warningNotice, 
  setHasAnalyzed, 
  onAnalysisCountChange, 
  isAdmin, 
  userRole,
  maxAnalysisCount,
  selectedSubtitle 
}) => {
  // State variables for managing the component's data and UI
  const [analysis, setAnalysis] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [analysisTime, setAnalysisTime] = useState(0);
  const [readingTime, setReadingTime] = useState(0);
  const [gender, setGender] = useState('');
  const [ageRange, setAgeRange] = useState('');
  const [maritalStatus, setMaritalStatus] = useState('');
  const [concern, setConcern] = useState('');
  const [job, setJob] = useState('');
  const [showWarning, setShowWarning] = useState(true);
  const [promptContent, setPromptContent] = useState('');
  const [buttonText, setButtonText] = useState('Luận giải');
  const [analysisCount, setAnalysisCount] = useState(0);
  const [showLimitMessage, setShowLimitMessage] = useState(false);
  const [specificModel, setSpecificModel] = useState('');
  const [showVIPPricing, setShowVIPPricing] = useState(false);
  const [userRoleState, setUserRoleState] = useState(userRole);
  const [isExpired, setIsExpired] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showAnalysisTime, setShowAnalysisTime] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [businessContext, setBusinessContext] = useState('');
  const [interactionGoal, setInteractionGoal] = useState('');
  const [responseTime, setResponseTime] = useState(0);
  const [category, setCategory] = useState('Đọc vị');
  const [showBusinessContext, setShowBusinessContext] = useState(true);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isEnhancingGoal, setIsEnhancingGoal] = useState(false);

  // State for AI clients
  const [aiClients, setAiClients] = useState({
    openai: null,
    deepseek: null,
    gemini: null,
    anthropic: null,
    qwen: null
  });

  // Replace the FIXED_MODELS constant with updated models and base URLs
  const FIXED_MODELS = {
    openai: {
      model: 'gpt-4o-2024-11-20',
      baseURL: 'https://api.openai.com/v1'
    },
    claude: {
      model: 'claude-3-5-sonnet-20241022',
      baseURL: 'https://api.anthropic.com'
    },
    gemini: {
      model: 'gemini-2.0-pro-exp-02-05'
    },
    deepseek: {
      model: 'deepseek-chat',
      baseURL: 'https://api.deepseek.com/v1'
    },
    qwen: {
      model: 'qwen-max',
      baseURL: 'https://dashscope-intl.aliyuncs.com/compatible-mode/v1'
    }
  };

  // Thêm constant cho default model
  const DEFAULT_AI_PLATFORM = 'qwen';
  const DEFAULT_AI_MODEL = 'qwen-max';

  // Initialize AI clients
  useEffect(() => {
    const initializeClients = async () => {
      try {
        // Initialize OpenAI client
        if (import.meta.env.VITE_OPENAI_API_KEY) {
          const openai = new OpenAI({
            apiKey: import.meta.env.VITE_OPENAI_API_KEY,
            baseURL: FIXED_MODELS.openai.baseURL,
            dangerouslyAllowBrowser: true
          });
          setAiClients(prev => ({ ...prev, openai }));
        }

        // Initialize Deepseek client
        if (import.meta.env.VITE_DEEPSEEK_API_KEY) {
          const deepseek = new OpenAI({
            baseURL: FIXED_MODELS.deepseek.baseURL,
            apiKey: import.meta.env.VITE_DEEPSEEK_API_KEY,
            dangerouslyAllowBrowser: true
          });
          setAiClients(prev => ({ ...prev, deepseek }));
        }

        // Initialize Gemini client
        if (import.meta.env.VITE_GEMINI_API_KEY) {
          const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY);
          const geminiModel = genAI.getGenerativeModel({ model: FIXED_MODELS.gemini.model });
          setAiClients(prev => ({ ...prev, gemini: geminiModel }));
        }

        // Initialize Claude client
        if (import.meta.env.VITE_ANTHROPIC_API_KEY) {
          try {
            const anthropic = new Anthropic({
              apiKey: import.meta.env.VITE_ANTHROPIC_API_KEY,
              baseURL: FIXED_MODELS.claude.baseURL,
              dangerouslyAllowBrowser: true
            });
            setAiClients(prev => ({ ...prev, anthropic }));
          } catch (error) {
            console.error('Error initializing Claude client:', error);
            setAiClients(prev => ({ ...prev, anthropic: { error: error.message } }));
          }
        }

        // Initialize Qwen client
        if (import.meta.env.VITE_QWEN_API_KEY) {
          const qwen = new OpenAI({
            baseURL: FIXED_MODELS.qwen.baseURL,
            apiKey: import.meta.env.VITE_QWEN_API_KEY,
            dangerouslyAllowBrowser: true
          });
          setAiClients(prev => ({ ...prev, qwen }));
        }
      } catch (error) {
        console.error('Error initializing AI clients:', error);
      }
    };

    initializeClients();
  }, []);

  // Get AI client
  const getClient = (model) => {
    const clientKey = {
      openai: 'openai',
      deepseek: 'deepseek',
      gemini: 'gemini',
      claude: 'anthropic',
      qwen: 'qwen'
    }[model];
    
    const client = aiClients[clientKey];
    if (!client) {
      throw new Error(`${model} client not initialized`);
    }
    return client;
  };

  // Reset analysis when resetAnalysis prop changes
  useEffect(() => {
    if (resetAnalysis) {
      setAnalysis('');
      setAnalysisTime(0);
      setReadingTime(0);
      setGender('');
      setAgeRange('');
      setMaritalStatus('');
      setConcern('');
      setJob('');
      setBusinessContext('');
      setInteractionGoal('');
      setCategory('');
      setShowWarning(true);
      setPromptContent('');
      setHasAnalyzed(false);
      setButtonText('Luận giải');
      setShowLimitMessage(false);
      setShowChat(false);
      setShowAnalysisTime(false);
      onResetComplete();
    }
  }, [resetAnalysis, onResetComplete, setHasAnalyzed]);

  // Add effect to reset category when switching from business to contemplation
  useEffect(() => {
    if (selectedSubtitle === 'contemplation') {
      setCategory('');
    }
  }, [selectedSubtitle]);

  // Fetch analysis count
  useEffect(() => {
    const fetchAnalysisCount = async () => {
      if (auth.currentUser) {
        const userData = await getUserData(auth.currentUser.uid);
        setAnalysisCount(userData?.analysisCount || 0);
        setShowLimitMessage((userData?.analysisCount || 0) >= (userData?.maxAnalysisCount || maxAnalysisCount));
      } else {
        setShowLimitMessage(false);
      }
    };
    fetchAnalysisCount();
  }, [auth.currentUser]);

  useEffect(() => {
    setShowLimitMessage(analysisCount >= maxAnalysisCount);
  }, [analysisCount, maxAnalysisCount]);

  // Format API response
  const formatResponse = (text) => {
    return text
      .replace(/^([0-9]+.)(?!\s)/gm, '$1 ')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/([^.!?])\n(?!\n)/g, '$1  \n')
      .trim();
  };

  // Calculate reading time
  const calculateReadingTime = (text) => {
    if (!text) return 0;
    const wordCount = text.trim().split(/\s+/).length;
    return Math.ceil(wordCount / 400);
  };

  // Add this function before the handleAnalyze function
  const updateUserRole = async (uid) => {
    try {
      const userDocRef = doc(db, 'users', uid);
      await updateDoc(userDocRef, {
        role: 'regular'
      });
      setUserRoleState('regular');
      onModelChange('qwen'); // Reset model về qwen
      return true;
    } catch (error) {
      console.error('Error updating user role:', error);
      return false;
    }
  };

  // Handle analysis process
  const handleAnalyze = async () => {
    if (analysisCount >= maxAnalysisCount) {
      if (userRole === 'vip' && auth.currentUser) {
        const updated = await updateUserRole(auth.currentUser.uid);
        if (updated) {
          setShowVIPPricing(true);
          return;
        }
      }
      setShowLimitMessage(true);
      return;
    }

    setIsLoading(true);
    setAnalysis('');
    setAnalysisTime(0);
    setReadingTime(0);
    setResponseTime(0);
    setHasAnalyzed(false);
    
    // Cập nhật loading message dựa vào category
    const loadingText = {
      'Đọc vị': 'Đang đọc vị...',
      'Nhập vai': 'Đang nhập vai...',
      'Lập kế hoạch': 'Đang lập kế hoạch...',
      'default': 'Đang luận giải...'
    }[category] || 'Đang luận giải...';
    
    setButtonText(loadingText);
    setShowAnalysisTime(false);
    setShowChat(false);

    const startTime = Date.now();

    try {
      const client = getClient(selectedModel);
      let completion;

      if (selectedModel === 'openai') {
        completion = await client.chat.completions.create({
          model: specificModel,
          messages: [
            {
              role: "system",
              content: "Từ bây giờ, bạn sẽ đóng vai là 1 người có khả năng suy nghĩ độc lập, bạn sẽ tự nói với chính mình trước khi phản hồi bất kỳ điều gì, tự nói với chính bản thân để coi nên trả lời thế nào cho đúng nhất, hợp lý nhất, tự biên tự diễn trước rồi mới trả lời. Sau khi nghĩ ra được câu trả lời, bạn lại tự đánh giá chi tiết câu trả lời lại coi đúng ý bạn và đúng ý người hỏi chưa, nếu chưa thì suy nghĩ lại và làm lại, lặp lại cho tới khi có câu trả lời đúng nhất."
            },
            {
              role: "user",
              content: promptContent
            }
          ],
          temperature: 0.7,
          max_tokens: 8192,
          presence_penalty: 0,
          frequency_penalty: 0
        });
      } else if (selectedModel === 'deepseek') {
        try {
          completion = await client.chat.completions.create({
            model: specificModel,
            messages: [
              {
                role: "system",
                content: "Từ bây giờ, bạn sẽ đóng vai là 1 người có khả năng suy nghĩ độc lập, bạn sẽ tự nói với chính mình trước khi phản hồi bất kỳ điều gì, tự nói với chính bản thân để coi nên trả lời thế nào cho đúng nhất, hợp lý nhất, tự biên tự diễn trước rồi mới trả lời. Sau khi nghĩ ra được câu trả lời, bạn lại tự đánh giá chi tiết câu trả lời lại coi đúng ý bạn và đúng ý người hỏi chưa, nếu chưa thì suy nghĩ lại và làm lại, lặp lại cho tới khi có câu trả lời đúng nhất."
              },
              {
                role: "user",
                content: promptContent
              }
            ],
            temperature: 0.7,
            max_tokens: 8192,
            presence_penalty: 0,
            frequency_penalty: 0,
            stream: false
          });

          if (!completion?.choices?.[0]?.message?.content) {
            throw new Error('Invalid response format from Deepseek API');
          }
        } catch (error) {
          console.error('Error with Deepseek analysis:', error);
          throw new Error(`Đã xảy ra lỗi khi phân tích: ${error.message}. Vui lòng thử lại.`);
        }
      } else if (selectedModel === 'gemini') {
        const genAI = new GoogleGenerativeAI(import.meta.env.VITE_GEMINI_API_KEY);
        const geminiModel = genAI.getGenerativeModel({ model: specificModel });
        const geminiResult = await geminiModel.generateContent(promptContent);
        completion = {
          choices: [{
            message: {
              content: geminiResult.response.text()
            }
          }]
        };
      } else if (selectedModel === 'claude') {
        try {
          if (!client || client.error) {
            throw new Error(client?.error || 'Claude client not initialized');
          }

          const claudeResult = await client.messages.create({
            model: specificModel,
            max_tokens: 8192,
            messages: [
              {
                role: "user",
                content: promptContent
              }
            ],
            temperature: 0.7
          });

          if (!claudeResult?.content?.[0]?.text) {
            throw new Error('Invalid response format from Claude API');
          }

          completion = {
            choices: [{
              message: {
                content: claudeResult.content[0].text
              }
            }]
          };
        } catch (error) {
          console.error('Error with Claude analysis:', error);
          throw new Error(`Đã xảy ra lỗi khi phân tích: ${error.message}. Vui lòng thử lại.`);
        }
      } else if (selectedModel === 'qwen') {
        try {
          completion = await client.chat.completions.create({
            model: specificModel,
            messages: [
              {
                role: "system",
                content: "Từ bây giờ, bạn sẽ đóng vai là 1 người có khả năng suy nghĩ độc lập, bạn sẽ tự nói với chính mình trước khi phản hồi bất kỳ điều gì, tự nói với chính bản thân để coi nên trả lời thế nào cho đúng nhất, hợp lý nhất, tự biên tự diễn trước rồi mới trả lời. Sau khi nghĩ ra được câu trả lời, bạn lại tự đánh giá chi tiết câu trả lời lại coi đúng ý bạn và đúng ý người hỏi chưa, nếu chưa thì suy nghĩ lại và làm lại, lặp lại cho tới khi có câu trả lời đúng nhất."
              },
              {
                role: "user",
                content: promptContent
              }
            ],
            temperature: 0.7,
            max_tokens: 8192,
            presence_penalty: 0,
            frequency_penalty: 0
          });

          if (!completion?.choices?.[0]?.message?.content) {
            throw new Error('Invalid response format from Qwen API');
          }
        } catch (error) {
          console.error('Error with Qwen analysis:', error);
          if (error.response) {
            console.error('Error response:', error.response.data);
          }
          throw new Error(`Đã xảy ra lỗi khi phân tích: ${error.message}. Vui lòng thử lại.`);
        }
      }

      if (!completion?.choices?.[0]?.message?.content) {
        throw new Error('Invalid API response format');
      }

      const endTime = Date.now();
      const timeTaken = (endTime - startTime) / 1000;

      const formattedResponse = formatResponse(completion.choices[0].message.content);
      const calculatedReadingTime = calculateReadingTime(formattedResponse);
      
      setAnalysis(formattedResponse);
      setAnalysisTime(timeTaken.toFixed(2));
      setResponseTime(timeTaken.toFixed(2));
      setReadingTime(calculatedReadingTime);
      setHasAnalyzed(true);
      setButtonText('Luận giải lại');
      setShowChat(true);
      setShowAnalysisTime(true);

      // Scroll to chat container smoothly
      setTimeout(() => {
        const chatContainer = document.querySelector('.chat-container');
        if (chatContainer) {
          const yOffset = -20;
          const y = chatContainer.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);

      if (auth.currentUser) {
        await incrementUserAnalysisCount(auth.currentUser.uid);
        const newCount = analysisCount + 1;
        setAnalysisCount(newCount);
        if (onAnalysisCountChange) {
          onAnalysisCountChange(newCount);
        }
      }
    } catch (error) {
      console.error('Analysis Error:', error);
      
      // Thêm thông báo lỗi chi tiết và hướng dẫn
      const errorMessage = `
# ⚠️ Đã xảy ra lỗi khi phân tích

## Nguyên nhân có thể:
- Hệ thống đang tải nặng
- Kết nối mạng không ổn định
- Lỗi từ máy chủ

## Bạn có thể thử:
1. **Đợi 1-2 phút** rồi thử phân tích lại
2. **Kiểm tra kết nối mạng** của bạn
3. **Làm mới trang** và thử lại

> 💡 Nếu vẫn gặp vấn đề, vui lòng liên hỗ trợ qua Zalo: ${contactNumber}

*Chúng tôi xin lỗi vì sự bất tiện này và đang nỗ lực khắc phục sớm nhất có thể.*
      `;
      
      setAnalysis(errorMessage);
      setButtonText('Thử lại');
    } finally {
      setIsLoading(false);
    }
  };

  // Add interval timer effect
  useEffect(() => {
    let timer;
    if (isLoading) {
      setElapsedTime(0);
      timer = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      setElapsedTime(0);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isLoading]);

  // Check if form is valid
  const isFormValid = () => {
    const baseFieldsValid = gender && ageRange && maritalStatus && job;
    
    if (selectedSubtitle === 'contemplation') {
      return baseFieldsValid && concern;
    } else {
      // Nếu đang ở chế độ business
      if (category === 'Đọc vị') {
        // Khi ở chế độ Đọc vị, chỉ cần các trường cơ bản
        return baseFieldsValid;
      } else if (showBusinessContext) {
        // Khi bật switch bối cảnh -> yêu cầu thêm businessContext và interactionGoal
        return baseFieldsValid && businessContext && interactionGoal;
      } else {
        // Khi tắt switch bối cảnh -> chỉ cần các trường cơ bản
        return baseFieldsValid;
      }
    }
  };

  // Handle button click
  const handleButtonClick = () => {
    if (isFormValid()) {
      setShowWarning(false);
      handleAnalyze();
    }
  };

  // Handle input changes
  const handleInputChange = (e, setter) => {
    const value = e.target.value;
    setter(value);
    
    // Create updated values based on which field changed
    const updatedValues = {
      category: e.target.id === 'category' ? value : category,
      gender: e.target.id === 'gender' ? value : gender,
      ageRange: e.target.id === 'ageRange' ? value : ageRange,
      maritalStatus: e.target.id === 'maritalStatus' ? value : maritalStatus,
      concern: e.target.id === 'concern' ? value : concern,
      job: e.target.id === 'job' ? value : job,
      businessContext: e.target.id === 'businessContext' ? value : businessContext,
      interactionGoal: e.target.id === 'interactionGoal' ? value : interactionGoal
    };

    // Call onPromptChange with all required parameters
    onPromptChange(
      updatedValues.category,
      updatedValues.gender,
      updatedValues.ageRange,
      updatedValues.maritalStatus,
      updatedValues.concern,
      updatedValues.job,
      updatedValues.businessContext,
      updatedValues.interactionGoal,
      dominantEnergy,
      combinedPairs,
      notificationList,
      ghiChuList,
      warningNotice
    );
  };

  // Update useEffect for model selection
  useEffect(() => {
    // Set the specific model based on selected platform
    setSpecificModel(FIXED_MODELS[selectedModel]?.model || DEFAULT_AI_MODEL);
  }, [selectedModel]);

  // Sửa hàm handleModelChange chỉ cho phép admin
  const handleModelChange = (e) => {
    if (isAdmin) {
      const newPlatform = e.target.value;
      onModelChange(newPlatform);
    }
  };

  // Update prompt content when inputs change
  useEffect(() => {
    try {
      // First validate phone number
      if (!phoneNumber || phoneNumber.length !== 10) {
        console.error('Invalid phone number:', phoneNumber);
        return;
      }

      // Then validate hexagramData
      if (!hexagramData) {
        console.error('hexagramData is null - phone number validation may have failed');
        setAnalysis('Đã xảy ra lỗi khi phân tích số điện thoại. Vui lòng nhập số điện thoại và thử lại.');
        return;
      }

      // Validate hexagramData structure
      if (!hexagramData.main?.name || !hexagramData.main?.meaning ||
          !hexagramData.transformed?.name || !hexagramData.transformed?.meaning ||
          !hexagramData.movingLine || !hexagramData.numberPairs) {
        console.error('Invalid hexagramData structure:', hexagramData);
        setAnalysis('Đã xảy ra lỗi khi phân tích dữ liệu quẻ dịch. Vui lòng thử lại.');
        return;
      }

      // Validate hexagram data matches phone number
      if (hexagramData.phoneNumber !== phoneNumber) {
        console.error('Phone number mismatch:', { 
          hexagramPhone: hexagramData.phoneNumber, 
          currentPhone: phoneNumber 
        });
        return;
      }

      try {
        // Select prompt generator based on category
        let generateSelectedPrompt;
        if (selectedSubtitle === 'business') {
          switch (category) {
            case 'Đọc vị':
              generateSelectedPrompt = generateBusinessPrompt;
              break;
            case 'Nhập vai':
              generateSelectedPrompt = generateRoleplayPrompt;
              break;
            case 'Lập kế hoạch':
              generateSelectedPrompt = generatePlanPrompt;
              break;
            default:
              generateSelectedPrompt = generateBusinessPrompt;
          }
        } else {
          generateSelectedPrompt = generateContemplationPrompt;
        }
        
        // Generate prompt with validated data
        const prompt = generateSelectedPrompt(
          hexagramData,
          gender,
          ageRange,
          maritalStatus,
          concern,
          job,
          businessContext,
          interactionGoal,
          dominantEnergy,
          combinedPairs,
          notificationList,
          ghiChuList,
          warningNotice
        );

        // Validate generated prompt
        if (!prompt) {
          console.error('Generated prompt is empty');
          setAnalysis('Đã xảy ra lỗi khi tạo nội dung phân tích. Vui lòng thử lại.');
          return;
        }

        setPromptContent(prompt);
      } catch (error) {
        console.error('Error generating prompt:', error);
        setAnalysis('Đã xảy ra lỗi khi tạo nội dung phân tích. Vui lòng thử lại.');
      }
    } catch (error) {
      console.error('Error in prompt generation:', error);
      setAnalysis('Đã xảy ra lỗi khi xử lý dữ liệu. Vui lòng thử lại.');
    }
  }, [hexagramData, phoneNumber, gender, ageRange, maritalStatus, concern, job, businessContext, interactionGoal, dominantEnergy, combinedPairs, notificationList, ghiChuList, warningNotice, selectedSubtitle, category]);

  // Add to useEffect for checking user data
  useEffect(() => {
    const checkUserStatus = async () => {
      const user = auth.currentUser;
      if (user) {
        const userData = await getUserData(user.uid);
        if (userData?.expirationDate) {
          const expirationDate = new Date(userData.expirationDate.toDate());
          setIsExpired(expirationDate < new Date() && userData.role === 'regular');
        }
      }
    };
    checkUserStatus();
  }, []);

  const contactName = "Lê Thế Bình";
  const contactNumber = "0982631339";
  const contactColor = "#ff0000";
  const remainingAnalyses = maxAnalysisCount - analysisCount;

  // Cập nhật useEffect để set button text dựa vào category
  useEffect(() => {
    const buttonTextMap = {
      'Đọc vị': 'Phân tích',
      'Nhập vai': 'Huấn luyện',
      'Lập kế hoạch': 'Lập kế hoạch',
      'default': 'Luận giải'
    };
    
    setButtonText(buttonTextMap[category] || buttonTextMap.default);
  }, [category]);

  // Reset business context fields when switch is turned off
  useEffect(() => {
    if (!showBusinessContext) {
      setBusinessContext('');
      setInteractionGoal('');
    }
  }, [showBusinessContext]);

  // Tự động tắt switch Bối cảnh kinh doanh khi chọn "Đọc vị"
  useEffect(() => {
    if (category === 'Đọc vị') {
      setShowBusinessContext(false);
    }
  }, [category]);

  // Thêm hàm xử lý enhance
  const handleEnhanceContext = async () => {
    if (!businessContext.trim() || isEnhancing) return;
    
    setIsEnhancing(true);
    try {
      const prompt = generateEnhancePrompt(businessContext);
      
      const client = aiClients.qwen;
      if (!client) throw new Error('Qwen client not initialized');

      const completion = await client.chat.completions.create({
        model: 'qwen-max',
        messages: [
          {
            role: "system",
            content: "Từ bây giờ, bạn sẽ đóng vai là 1 người có khả năng suy nghĩ độc lập, bạn sẽ tự nói với chính mình trước khi phản hồi bất kỳ điều gì, tự nói với chính bản thân để coi nên trả lời thế nào cho đúng nhất, hợp lý nhất, tự biên tự diễn trước rồi mới trả lời. Sau khi nghĩ ra được câu trả lời, bạn lại tự đánh giá chi tiết câu trả lời lại coi đúng ý bạn và đúng ý người hỏi chưa, nếu chưa thì suy nghĩ lại và làm lại, lặp lại cho tới khi có câu trả lời đúng nhất."
          },
          {
            role: "user",
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 2000
      });

      if (completion?.choices?.[0]?.message?.content) {
        setBusinessContext(completion.choices[0].message.content);
        
        // Cập nhật số lần phân tích trong database
        if (auth.currentUser) {
          const userData = await getUserData(auth.currentUser.uid);
          const newCount = (userData?.analysisCount || 0) + 1;
          const userDocRef = doc(db, 'users', auth.currentUser.uid);
          await updateDoc(userDocRef, {
            analysisCount: newCount,
            lastAnalysisTime: new Date().toISOString()
          });
          setAnalysisCount(newCount);
          onAnalysisCountChange?.(newCount);
        }
      }
    } catch (error) {
      console.error('Error enhancing context:', error);
      // Có thể thêm thông báo lỗi cho người dùng ở đây
    } finally {
      setIsEnhancing(false);
    }
  };

  // Thêm hàm xử lý enhance cho interactionGoal
  const handleEnhanceInteractionGoal = async () => {
    if (!interactionGoal.trim() || isEnhancingGoal) return;
    
    setIsEnhancingGoal(true);
    try {
      const prompt = generateEnhanceInteractionGoalPrompt(interactionGoal);
      
      const client = aiClients.qwen;
      if (!client) throw new Error('Qwen client not initialized');

      const completion = await client.chat.completions.create({
        model: 'qwen-max',
        messages: [
          {
            role: "system",
            content: "Từ bây giờ, bạn sẽ đóng vai là 1 người có khả năng suy nghĩ độc lập, bạn sẽ tự nói với chính mình trước khi phản hồi bất kỳ điều gì, tự nói với chính bản thân để coi nên trả lời thế nào cho đúng nhất, hợp lý nhất, tự biên tự diễn trước rồi mới trả lời. Sau khi nghĩ ra được câu trả lời, bạn lại tự đánh giá chi tiết câu trả lời lại coi đúng ý bạn và đúng ý người hỏi chưa, nếu chưa thì suy nghĩ lại và làm lại, lặp lại cho tới khi có câu trả lời đúng nhất."
          },
          {
            role: "user",
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 2000
      });

      if (completion?.choices?.[0]?.message?.content) {
        setInteractionGoal(completion.choices[0].message.content);
        
        // Cập nhật số lần phân tích trong database
        if (auth.currentUser) {
          const userData = await getUserData(auth.currentUser.uid);
          const newCount = (userData?.analysisCount || 0) + 1;
          const userDocRef = doc(db, 'users', auth.currentUser.uid);
          await updateDoc(userDocRef, {
            analysisCount: newCount,
            lastAnalysisTime: new Date().toISOString()
          });
          setAnalysisCount(newCount);
          onAnalysisCountChange?.(newCount);
        }
      }
    } catch (error) {
      console.error('Error enhancing interaction goal:', error);
    } finally {
      setIsEnhancingGoal(false);
    }
  };

  return (
    <div className="analysis-section">
      <div className="analysis-content">
        <h2 className="section-title">
          {selectedSubtitle === 'business' ? '🔮 Cung Cấp Thông Tin Chủ Số' : '🔮 Luận Giải Ý Nghĩa Số'} {phoneNumber}
        </h2>
        {showWarning && (
          <p className="warning-message">
            Vui lòng nhập ĐẦY ĐỦ thông tin của chủ số để bắt đầu luận giải
          </p>
        )}

        <div className="additional-inputs">
          {selectedSubtitle === 'business' && (isAdmin || userRole === 'pioneer') && (
            <div className="input-group-row category-row">
              <div className="input-group" style={{ width: '100%' }}>
                <label htmlFor="category">Danh mục:</label>
                <select id="category" value={category} onChange={(e) => handleInputChange(e, setCategory)}>
                  <option value="Đọc vị">Đọc vị</option>
                  <option value="Nhập vai">Nhập vai</option>
                  <option value="Lập kế hoạch">Lập kế hoạch</option>
                </select>
              </div>
            </div>
          )}

          <div className="input-group-row">
            <div className="input-group">
              <label htmlFor="gender">Giới tính:</label>
              <select id="gender" value={gender} onChange={(e) => handleInputChange(e, setGender)}>
                <option value="">Chọn giới tính</option>
                <option value="Nam">Nam</option>
                <option value="Nữ">Nữ</option>
              </select>
            </div>

            <div className="input-group">
              <label htmlFor="ageRange">Độ tuổi:</label>
              <select id="ageRange" value={ageRange} onChange={(e) => handleInputChange(e, setAgeRange)}>
                <option value="">Chọn độ tuổi</option>
                <option value="19 - 23 tuổi">19 - 23 tuổi</option>
                <option value="24 - 29 tuổi">24 - 29 tuổi</option>
                <option value="30 - 35 tuổi">30 - 35 tuổi</option>
                <option value="36 - 40 tuổi">36 - 40 tuổi</option>
                <option value="41 - 45 tuổi">41 - 45 tuổi</option>
                <option value="46 - 50 tuổi">46 - 50 tuổi</option>
                <option value="51 - 55 tuổi">51 - 55 tuổi</option>
                <option value="56 - 60 tuổi">56 - 60 tuổi</option>
              </select>
            </div>

            <div className="input-group">
              <label htmlFor="maritalStatus">Tình trạng hôn nhân:</label>
              <select id="maritalStatus" value={maritalStatus} onChange={(e) => handleInputChange(e, setMaritalStatus)}>
                <option value="">Chọn tình trạng hôn nhân</option>
                <option value="Chưa từng yêu ai">Chưa từng yêu ai</option>
                <option value="Đã yêu nhưng hiện tại chưa yêu ai">Đã yêu nhưng hiện tại chưa yêu ai</option>
                <option value="Đang yêu">Đang yêu</option>
                <option value="Đã đính hôn - chưa kết hôn">Đã đính hôn - chưa kết hôn</option>
                <option value="Đã lập gia đình - chưa có con">Đã lập gia đình - chưa có con</option>
                <option value="Đã lập gia đình - có 1 con">Đã lập gia đình - có 1 con</option>
                <option value="Đã lập gia đình - có 2 con">Đã lập gia đình - có 2 con</option>
                <option value="Đã lập gia đình - có hơn 2 con">Đã lập gia đình - có hơn 2 con</option>
                <option value="Đã lập gia đình lần 2 hoặc hơn">Đã lập gia đình lần 2 hoặc hơn</option>
                <option value="Ly hôn - sống đơn thân">Ly hôn - sống đơn thân</option>
                <option value="Ly hôn - đang yêu">Ly hôn - đang yêu</option>
                <option value="Vợ/chồng mất - sống đơn thân">Vợ/chồng mất - sống đơn thân</option>
                <option value="Sống chung như vợ chồng nhưng chưa đăng ký kết hôn">Sống chung như vợ chồng nhưng chưa đăng ký kết hôn</option>
                <option value="Sống chung như vợ chồng và có con nhưng chưa đăng ký kết hôn">Sống chung như vợ chồng và có con nhưng chưa đăng ký kết hôn</option>
              </select>
            </div>
          </div>

          <div className="input-group full-width">
            <label htmlFor="job">Công việc chính:</label>
            <input 
              type="text" 
              id="job" 
              value={job} 
              onChange={(e) => handleInputChange(e, setJob)} 
              placeholder="Mô tả công việc hiện tại của chủ số"
              className="text-input"
            />
          </div>

          {selectedSubtitle === 'business' && (isAdmin || userRole === 'pioneer') && (
            <div className="input-group-row" style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
              <div className="switch-container">
                <div className="switch-title-group">
                  <span className="switch-title">Bối cảnh kinh doanh</span>
                  <Switch
                    isSelected={showBusinessContext}
                    onChange={setShowBusinessContext}
                  />
                </div>
              </div>
            </div>
          )}

          {selectedSubtitle === 'business' && showBusinessContext && (isAdmin || userRole === 'pioneer') && (
            <>
              <div className="input-group full-width">
                <label htmlFor="businessContext">Bối cảnh kinh doanh:</label>
                <div className="input-with-icon">
                  <textarea
                    id="businessContext"
                    value={businessContext}
                    onChange={(e) => setBusinessContext(e.target.value)}
                    placeholder="Mô tả bối cảnh kinh doanh..."
                    rows="3"
                  />
                  <Tooltip content="Tăng cường độ chi tiết cho bối cảnh">
                    <button
                      className={`enhance-button ${isEnhancing ? 'enhancing' : ''}`}
                      onClick={handleEnhanceContext}
                      disabled={isEnhancing || !businessContext.trim() || analysisCount >= maxAnalysisCount}
                    >
                      <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 512 512" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path 
                          d="M259.92,262.91,216.4,149.77a9,9,0,0,0-16.8,0L156.08,262.91a9,9,0,0,1-5.17,5.17L37.77,311.6a9,9,0,0,0,0,16.8l113.14,43.52a9,9,0,0,1,5.17,5.17L199.6,490.23a9,9,0,0,0,16.8,0l43.52-113.14a9,9,0,0,1,5.17-5.17L378.23,328.4a9,9,0,0,0,0-16.8L265.09,268.08A9,9,0,0,1,259.92,262.91Z" 
                          stroke="currentColor" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="32"
                        />
                        <path 
                          d="M108 68L88 16L68 68L16 88L68 108L88 160L108 108L160 88L108 68" 
                          stroke="currentColor" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="32"
                        />
                        <path 
                          d="M426.67 117.33L400 48L373.33 117.33L304 144L373.33 170.67L400 240L426.67 170.67L496 144L426.67 117.33" 
                          stroke="currentColor" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="32"
                        />
                      </svg>
                    </button>
                  </Tooltip>
                </div>
              </div>

              <div className="input-group full-width">
                <label htmlFor="interactionGoal">Mục tiêu tương tác:</label>
                <div className="input-with-icon">
                  <textarea 
                    id="interactionGoal" 
                    value={interactionGoal} 
                    onChange={(e) => handleInputChange(e, setInteractionGoal)} 
                    placeholder="Mô tả mục tiêu tương tác với chủ số của bạn"
                    className="text-input interaction-goal"
                    rows="3"
                  />
                  <Tooltip content="Tăng cường độ chi tiết cho mục tiêu">
                    <button
                      className={`enhance-button ${isEnhancingGoal ? 'enhancing' : ''}`}
                      onClick={handleEnhanceInteractionGoal}
                      disabled={isEnhancingGoal || !interactionGoal.trim() || analysisCount >= maxAnalysisCount}
                    >
                      <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 512 512" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path 
                          d="M259.92,262.91,216.4,149.77a9,9,0,0,0-16.8,0L156.08,262.91a9,9,0,0,1-5.17,5.17L37.77,311.6a9,9,0,0,0,0,16.8l113.14,43.52a9,9,0,0,1,5.17,5.17L199.6,490.23a9,9,0,0,0,16.8,0l43.52-113.14a9,9,0,0,1,5.17-5.17L378.23,328.4a9,9,0,0,0,0-16.8L265.09,268.08A9,9,0,0,1,259.92,262.91Z" 
                          stroke="currentColor" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="32"
                        />
                        <path 
                          d="M108 68L88 16L68 68L16 88L68 108L88 160L108 108L160 88L108 68" 
                          stroke="currentColor" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="32"
                        />
                        <path 
                          d="M426.67 117.33L400 48L373.33 117.33L304 144L373.33 170.67L400 240L426.67 170.67L496 144L426.67 117.33" 
                          stroke="currentColor" 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth="32"
                        />
                      </svg>
                    </button>
                  </Tooltip>
                </div>
              </div>
            </>
          )}

          {selectedSubtitle === 'contemplation' && (
            <div className="input-group-row" style={{ marginBottom: '1rem' }}>
              <div className="input-group" style={{ flex: 1 }}>
                <label htmlFor="concern">Vấn đề quan tâm:</label>
                <select id="concern" value={concern} onChange={(e) => handleInputChange(e, setConcern)}>
                  <option value="">Chọn vấn đề bạn quan tâm</option>
                  <option value="Sự nghiệp - Công việc">Sự nghiệp - Công việc</option>
                  <option value="Tài chính - Tiền bạc">Tài chính - Tiền bạc</option>
                  <option value="Cơ hội kinh doanh">Cơ hội kinh doanh</option>
                  <option value="Tình yêu - Hôn nhân">Tình yêu - Hôn nhân</option>
                  <option value="Gia đình - Mối quan hệ">Gia đình - Mối quan hệ</option>
                  <option value="Sức khỏe - Tinh thần">Sức khỏe - Tinh thần</option>
                  <option value="Phát triển bản thân">Phát triển bản thân</option>
                  <option value="Học tập - Giáo dục">Học tập - Giáo dục</option>
                  <option value="Định hướng cuộc sống">Định hướng cuộc sống</option>
                  <option value="Đầu tư - Bất động sản">Đầu tư - Bất động sản</option>
                </select>
              </div>
            </div>
          )}
          {isAdmin && (
            <div className="input-group-row" style={{ marginBottom: '1rem' }}>
              <div className="input-group" style={{ flex: 1 }}>
                <label htmlFor="aiModel">Chọn Chuyên Gia Luận Giải:</label>
                <select 
                  id="aiModel" 
                  value={selectedModel} 
                  onChange={handleModelChange}
                >
                  <option value="qwen">Toàn Tri</option>
                  <option value="gemini">Minh Triết</option>
                  <option value="openai">Uyên Bác</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <button
            className={`ai-button ${isLoading ? 'loading' : ''}`}
            onClick={handleButtonClick}
            disabled={isLoading || !isFormValid() || analysisCount >= maxAnalysisCount || isExpired}
          >
            {isLoading ? (
              <div className="loading-container">
                <div className="loading-message">
                  {buttonText}, bạn vui lòng đợi 30-60 giây.
                  <div style={{ fontSize: '0.9em', marginTop: '8px' }}>
                    Thời gian phân tích: {elapsedTime} giây
                  </div>
                </div>
                <div className="loading-spinner"></div>
              </div>
            ) : (
              <>
                <span className="ai-button-text">
                  {buttonText}
                  {analysisCount > 0 && maxAnalysisCount && (
                    <span className="analysis-count">
                      (đã dùng {analysisCount}/{maxAnalysisCount} lượt)
                    </span>
                  )}
                </span>
                {buttonText === 'Thử lại' && (
                  <div style={{ fontSize: '0.9em', color: '#666', marginTop: '8px' }}>
                    Nếu chưa hài lòng, bạn có thể thử lại câu trả lời khác.
                  </div>
                )}
              </>
            )}
          </button>
          {(analysisCount >= maxAnalysisCount || showLimitMessage) && (
            <div className="limit-message-container">
              <i className="fas fa-exclamation-circle" style={{ color: '#ff6b6b' }}></i>
              <p className="limit-message">
                Tài khoản của bạn đã hết hạn/lượt
              </p>
              <button 
                className="unlock-vip-button-large"
                onClick={() => setShowVIPPricing(true)}
              >
                👑 Nâng cấp VIP ngay
              </button>
            </div>
          )}

          {isExpired && (
            <div className="analysis-expiration-notice">
              <div className="expired-content">
                <i className="fas fa-exclamation-circle"></i>
                Tài khoản của bạn đã hết hạn sử dụng
              </div>
              <button 
                className="unlock-vip-button-large"
                onClick={() => setShowVIPPricing(true)}
              >
                👑 Nâng cấp VIP ngay
              </button>
            </div>
          )}
        </div>

        {/* Add timing display */}
        {showAnalysisTime && responseTime > 0 && (
          <div className="analysis-timing">
            <p className="timing-info">
              ⏱️ Thời gian phân tích: {responseTime} giây
            </p>
          </div>
        )}

      </div>
      
      {showChat && (
        <div className="chat-section">
          <AIChat 
            analysis={analysis} 
            aiClient={getClient(selectedModel)}
            selectedModel={selectedModel}
            specificModel={specificModel}
            onAnalysisCountChange={onAnalysisCountChange}
            analysisCount={analysisCount}
            setAnalysisCount={setAnalysisCount}
            hexagramData={hexagramData}
            gender={gender}
            ageRange={ageRange}
            maritalStatus={maritalStatus}
            job={job}
            concern={concern}
            dominantEnergy={dominantEnergy}
            combinedPairs={combinedPairs}
            notificationList={notificationList}
            ghiChuList={ghiChuList}
            warningNotice={warningNotice}
            maxAnalysisCount={maxAnalysisCount}
            userRole={userRole}
          />
        </div>
      )}

      {showVIPPricing && <VIPPricing onClose={() => setShowVIPPricing(false)} />}
    </div>
  );
};

export default AIAnalysis;
